import { apiRequest } from './authService';

/**
 * Kassa (Register) service.
 * A daily cash register: each day has an opening balance per currency
 * (USD / SRD / EUR) that resets every day, plus transactions that add
 * ('recebido') or subtract ('pagamento') from the day's saldo.
 */

/**
 * Get everything for one day: opening balances (null if not set yet),
 * transactions, and the computed saldo per currency.
 * @param {string} date YYYY-MM-DD
 */
export async function getDay(date, register = 'main') {
  try {
    const response = await apiRequest('kassa/day', {
      method: 'POST',
      body: JSON.stringify({ date, register })
    });
    if (response.success && response.data) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to load day' };
  } catch (e) {
    console.error('kassa getDay error:', e);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

/**
 * Lightweight change signal for a day (for polling-based live sync).
 * @param {string} date
 */
export async function getDayVersion(date, register = 'main') {
  try {
    const response = await apiRequest('kassa/day-version', {
      method: 'POST',
      body: JSON.stringify({ date, register })
    });
    if (response.success && response.data) {
      return { success: true, data: response.data };
    }
    return { success: false };
  } catch (e) {
    // Silent — polling failures shouldn't spam the console/UI.
    return { success: false };
  }
}

/**
 * Set (or update) the opening balances for a day.
 * @param {string} date
 * @param {{usd:number, srd:number, eur:number}} opening
 */
export async function setOpening(date, { usd, srd, eur }, register = 'main') {
  try {
    const response = await apiRequest('kassa/set-opening', {
      method: 'POST',
      body: JSON.stringify({ date, usd, srd, eur, register })
    });
    if (response.success) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to save opening balances' };
  } catch (e) {
    console.error('kassa setOpening error:', e);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

/**
 * Add a transaction to a day.
 * @param {string} date
 * @param {'pagamento'|'recebido'} type
 * @param {string} description
 * @param {{usd:number, srd:number, eur:number}} amounts
 */
export async function addTransaction(date, type, description, { usd, srd, eur, recvCurrency, recvAmount, custodyCustomerId, custodyDir } = {}, register = 'main') {
  try {
    const response = await apiRequest('kassa/add-transaction', {
      method: 'POST',
      body: JSON.stringify({
        date, type, description,
        amount_usd: usd, amount_srd: srd, amount_eur: eur,
        recv_currency: recvCurrency || null,
        recv_amount: recvAmount || 0,
        custody_customer_id: custodyCustomerId || null,
        custody_dir: custodyDir || null,
        register
      })
    });
    if (response.success && response.data) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to add transaction' };
  } catch (e) {
    console.error('kassa addTransaction error:', e);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

/**
 * Update an existing transaction.
 * @param {number|string} id
 * @param {{type?, description?, usd?, srd?, eur?}} fields
 */
export async function updateTransaction(id, fields, register = 'main') {
  try {
    const body = { id, register };
    if (fields.type !== undefined) body.type = fields.type;
    if (fields.description !== undefined) body.description = fields.description;
    if (fields.usd !== undefined) body.amount_usd = fields.usd;
    if (fields.srd !== undefined) body.amount_srd = fields.srd;
    if (fields.eur !== undefined) body.amount_eur = fields.eur;
    if (fields.recvCurrency !== undefined) body.recv_currency = fields.recvCurrency;
    if (fields.recvAmount !== undefined) body.recv_amount = fields.recvAmount;
    if (fields.custodyCustomerId !== undefined) body.custody_customer_id = fields.custodyCustomerId;
    if (fields.custodyDir !== undefined) body.custody_dir = fields.custodyDir;

    const response = await apiRequest('kassa/update-transaction', {
      method: 'POST',
      body: JSON.stringify(body)
    });
    if (response.success && response.data) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to update transaction' };
  } catch (e) {
    console.error('kassa updateTransaction error:', e);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

/**
 * Get the day's closing (saved bill counts if any) + the current live saldo
 * and the denomination list.
 * @param {string} date
 */
export async function getClosing(date, register = 'main') {
  try {
    const response = await apiRequest('kassa/get-closing', {
      method: 'POST',
      body: JSON.stringify({ date, register })
    });
    if (response.success && response.data) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to load closing' };
  } catch (e) {
    console.error('kassa getClosing error:', e);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

/**
 * Save (upsert) the day's closing count.
 * @param {string} date
 * @param {{usd:object, srd:object, eur:object}} counts  per-currency {denom: count}
 */
export async function saveClosing(date, counts, close = true, register = 'main') {
  try {
    const response = await apiRequest('kassa/save-closing', {
      method: 'POST',
      body: JSON.stringify({ date, counts, close: close ? 1 : 0, register })
    });
    if (response.success) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to save closing' };
  } catch (e) {
    console.error('kassa saveClosing error:', e);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

/** Reopen (unlock) a closed day so it can be edited again. Requires the manager password. */
export async function reopenClosing(date, password, register = 'main') {
  try {
    const response = await apiRequest('kassa/reopen-closing', {
      method: 'POST',
      body: JSON.stringify({ date, password, register })
    });
    if (response.success) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to reopen' };
  } catch (e) {
    console.error('kassa reopenClosing error:', e);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

/**
 * Global search over a register's transactions (all days).
 * @param {string} query        text to match in the description
 * @param {string} type         '' | 'pagamento' | 'recebido' | 'cambio'
 * @param {{dateFrom?:string, dateTo?:string, limit?:number}} opts
 * @param {string} register
 */
export async function searchTransactions(query, type, { dateFrom, dateTo, limit } = {}, register = 'main') {
  try {
    const response = await apiRequest('kassa/search-transactions', {
      method: 'POST',
      body: JSON.stringify({
        query: query || '',
        type: type || '',
        date_from: dateFrom || null,
        date_to: dateTo || null,
        limit: limit || 200,
        register,
      })
    });
    if (response.success && response.data) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Search failed' };
  } catch (e) {
    console.error('kassa searchTransactions error:', e);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

export async function deleteTransaction(id, register = 'main') {
  try {
    const response = await apiRequest('kassa/delete-transaction', {
      method: 'POST',
      body: JSON.stringify({ id, register })
    });
    if (response.success) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to delete transaction' };
  } catch (e) {
    console.error('kassa deleteTransaction error:', e);
    return { success: false, message: 'Network error. Please try again.' };
  }
}
